import { useEffect, useId, useState } from 'react';
import { Group, Panel, Separator, usePanelCallbackRef } from 'react-resizable-panels';
import { ChevronsRight, GripVertical } from 'lucide-react';
import { VisuallyHidden } from 'radix-ui';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { useIsDesktop } from '../../hooks/useIsDesktop';

interface TwoPanelLayoutProps {
  /** Sidebar rendered on desktop (md+). */
  sidebar: React.ReactNode;
  /** Content for the mobile Sheet drawer. Defaults to `sidebar` if omitted. */
  mobileSidebar?: React.ReactNode;
  /** Whether the mobile drawer is open. */
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Whether the sidebar is in the collapsed state. Used to sync with the Panel width. */
  sidebarCollapsed?: boolean;
  /** Called when the panel collapse state changes (e.g. via drag). Keeps parent in sync. */
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  /** Key for persisting sidebar width in localStorage. */
  autoSaveId?: string;
  /** Default sidebar width in pixels (default: 288). */
  defaultSidebarWidth?: number;
  /** Minimum sidebar width in pixels (default: 160). */
  minSidebarWidth?: number;
  /** Maximum sidebar width in pixels (default: 480). */
  maxSidebarWidth?: number;
  /**
   * Width the sidebar collapses to in pixels (default: 48 — icon rail).
   * Pass 0 to fully hide the sidebar when collapsed.
   */
  collapsedSidebarWidth?: number;
}

const COLLAPSED_WIDTH = 48;

/**
 * Shared shell for pages that follow the sidebar + detail master-detail pattern.
 * On desktop, renders a resizable two-panel layout using react-resizable-panels.
 * On mobile, the sidebar lives inside a Sheet drawer.
 *
 * Children are rendered exactly once regardless of breakpoint.
 */
export function TwoPanelLayout({
  sidebar,
  mobileSidebar,
  sidebarOpen,
  onSidebarOpenChange,
  children,
  sidebarCollapsed = false,
  onSidebarCollapsedChange,
  autoSaveId,
  defaultSidebarWidth = 288,
  minSidebarWidth = 160,
  maxSidebarWidth = 480,
  collapsedSidebarWidth = COLLAPSED_WIDTH,
}: TwoPanelLayoutProps) {
  const isDesktop = useIsDesktop();
  const generatedId = useId();

  const [sidebarPanel, setSidebarPanel] = usePanelCallbackRef();

  const [initialSidebarWidth] = useState(() => {
    if (!autoSaveId) return defaultSidebarWidth;
    try {
      const v = localStorage.getItem(`twopanel-${autoSaveId}`);
      return v ? Number(v) : defaultSidebarWidth;
    } catch {
      return defaultSidebarWidth;
    }
  });

  // Sync collapsed state → Panel collapse/expand
  useEffect(() => {
    if (!sidebarPanel) return;
    if (sidebarCollapsed) {
      sidebarPanel.collapse();
    } else {
      sidebarPanel.expand();
    }
  }, [sidebarCollapsed, sidebarPanel]);

  if (isDesktop) {
    return (
      <Group orientation="horizontal" id={autoSaveId ?? generatedId} className="h-full bg-panel">
        <Panel
          id="sidebar"
          panelRef={setSidebarPanel}
          defaultSize={initialSidebarWidth}
          minSize={minSidebarWidth}
          maxSize={maxSidebarWidth}
          collapsible
          collapsedSize={collapsedSidebarWidth}
          groupResizeBehavior="preserve-pixel-size"
          onResize={(size) => {
            const nowCollapsed = size.inPixels <= collapsedSidebarWidth;
            if (onSidebarCollapsedChange) {
              onSidebarCollapsedChange(nowCollapsed);
            }
            if (!autoSaveId || nowCollapsed) return;
            try {
              localStorage.setItem(`twopanel-${autoSaveId}`, String(size.inPixels));
            } catch {
              /* noop */
            }
          }}
        >
          {/* The content-top banner slot (guided tutorials) lives inside the
              sidebar's own SidebarHeader so it sits below the panel title and
              above the search/filter row, rather than above the whole panel. */}
          <div className="h-full w-full overflow-hidden">{sidebar}</div>
        </Panel>
        <ResizeHandle
          collapsedToHidden={sidebarCollapsed && collapsedSidebarWidth === 0}
          onExpand={() => {
            sidebarPanel?.expand();
            onSidebarCollapsedChange?.(false);
          }}
        />
        <Panel id="detail">
          <div className="flex h-full flex-col overflow-hidden">{children}</div>
        </Panel>
      </Group>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-panel">
      <Sheet open={sidebarOpen} onOpenChange={onSidebarOpenChange}>
        <SheetContent side="left" showCloseButton={false} className="w-[300px] p-0">
          <VisuallyHidden.Root>
            <SheetTitle>Navigation</SheetTitle>
          </VisuallyHidden.Root>
          {mobileSidebar ?? sidebar}
        </SheetContent>
      </Sheet>
      {/* Mobile lacks a persistent sidebar (the drawer is closed by default),
          so the banner sits above the page content — the same place the
          global slot used to live before banners moved into the layouts. */}
      <div data-page-banner-region="" className="shrink-0 empty:hidden" />
      {children}
    </div>
  );
}

function ResizeHandle({
  className,
  collapsedToHidden,
  onExpand,
}: {
  className?: string;
  /** True when the sidebar is collapsed all the way to 0px (fully hidden). */
  collapsedToHidden?: boolean;
  onExpand?: () => void;
}) {
  return (
    <Separator
      className={cn(
        'group relative flex w-1.5 items-center justify-center bg-transparent',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
      aria-label="Resize sidebar"
    >
      <div
        className={cn(
          'absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-transparent to-transparent transition-colors',
          collapsedToHidden
            ? 'via-border/70 group-hover:via-border'
            : 'via-border/40 group-hover:via-border/70',
        )}
      />
      {collapsedToHidden && onExpand ? (
        // The edge itself is the reopen affordance for a fully hidden
        // sidebar: a persistent pull tab on the same boundary the user
        // dragged to close it. Click expands; dragging still resizes.
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onExpand}
              aria-label="Expand sidebar"
              className="z-10 flex h-10 w-3.5 items-center justify-center rounded-full border border-border/40 bg-background text-muted-foreground opacity-50 shadow-sm transition-[color,background-color,border-color,opacity] hover:border-border hover:bg-accent hover:text-foreground hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <ChevronsRight className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Expand sidebar</TooltipContent>
        </Tooltip>
      ) : (
        <div
          data-testid="sidebar-resize-grip"
          className="z-10 flex h-5 w-2.5 items-center justify-center rounded-sm border border-border/30 bg-background/80 text-muted-foreground opacity-50 backdrop-blur transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          <GripVertical className="h-3 w-3" />
        </div>
      )}
    </Separator>
  );
}
